import React from "react";

/**
 * Seletor de mês da lente Mês.
 *
 * Abre **sempre no mês corrente** — o caso de uso é acompanhar o mês em andamento; olhar meses
 * fechados é a exceção. Por isso não guardamos a escolha entre visitas: voltar à home amanhã tem
 * de mostrar hoje, não o mês que alguém abriu semana passada para conferir uma coisa.
 *
 * A lista cobre os últimos 12 meses (a mesma janela do histórico) e não oferece meses futuros:
 * não há o que apurar num mês que não começou.
 */

const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

export interface MonthValue {
  month: number;
  year: number;
}

/** Mês corrente — o padrão de abertura. */
export const currentMonth = (): MonthValue => {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

const buildOptions = (count = 12): MonthValue[] => {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  });
};

const key = (v: MonthValue) => `${v.year}-${String(v.month).padStart(2, "0")}`;

interface Props {
  value: MonthValue;
  onChange: (v: MonthValue) => void;
  /** Rótulo do progresso do mês (ex.: "dia útil 15 de 21"); some em mês fechado. */
  progressLabel?: string;
  disabled?: boolean;
}

const MonthPicker: React.FC<Props> = ({ value, onChange, progressLabel, disabled }) => {
  const options = buildOptions();
  const isCurrent = key(value) === key(currentMonth());

  return (
    <div className="flex items-center gap-2">
      <select
        aria-label="Mês de referência"
        value={key(value)}
        disabled={disabled}
        onChange={(e) => {
          const [y, m] = e.target.value.split("-");
          onChange({ month: Number(m), year: Number(y) });
        }}
        className="cursor-pointer rounded-full border border-gray-200 bg-white px-4 py-2 font-mono text-[11.5px] text-gray-700 transition-colors hover:border-dv-blue-400 focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200"
      >
        {options.map((o) => (
          <option key={key(o)} value={key(o)}>
            {MONTHS[o.month - 1]} {o.year}
          </option>
        ))}
      </select>

      {isCurrent && progressLabel && (
        <span className="hidden font-mono text-[11.5px] text-gray-500 dark:text-gray-400 sm:inline">
          {progressLabel}
        </span>
      )}
      {!isCurrent && (
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:bg-navy-900 dark:text-gray-400">
          fechado
        </span>
      )}
    </div>
  );
};

export default MonthPicker;
